import React from 'react';

export interface PulseIndicatorProps {
  size?: 'small' | 'medium' | 'large';
  color?: string;
  className?: string;
}

/**
 * Animated pulse indicator for voice recording
 *
 * Shows 3 concentric circles with pulsing animation
 *
 * @example
 * ```tsx
 * <PulseIndicator size="medium" color="#EF4444" />
 * ```
 */
export const PulseIndicator: React.FC<PulseIndicatorProps> = ({
  size = 'medium',
  color = '#EF4444',
  className = '',
}) => {
  const sizeMap = {
    small: {
      container: 24,
      circle1: 8,
      circle2: 16,
      circle3: 24,
    },
    medium: {
      container: 40,
      circle1: 12,
      circle2: 24,
      circle3: 40,
    },
    large: {
      container: 64,
      circle1: 20,
      circle2: 40,
      circle3: 64,
    },
  };

  const sizes = sizeMap[size];

  return (
    <div
      className={`relative inline-flex items-center justify-center ${className}`}
      style={{
        width: sizes.container,
        height: sizes.container,
      }}
    >
      {/* Center dot */}
      <div
        className="absolute rounded-full"
        style={{
          width: sizes.circle1,
          height: sizes.circle1,
          backgroundColor: color,
          zIndex: 3,
        }}
      />

      {/* First pulse ring */}
      <div
        className="absolute rounded-full animate-pulse-ring-1"
        style={{
          width: sizes.circle2,
          height: sizes.circle2,
          border: `2px solid ${color}`,
          opacity: 0.6,
          zIndex: 2,
        }}
      />

      {/* Second pulse ring */}
      <div
        className="absolute rounded-full animate-pulse-ring-2"
        style={{
          width: sizes.circle3,
          height: sizes.circle3,
          border: `2px solid ${color}`,
          opacity: 0.4,
          zIndex: 1,
        }}
      />

      {/* Add custom animations */}
      <style>{`
        @keyframes pulse-ring-1 {
          0%, 100% {
            transform: scale(0.95);
            opacity: 0.6;
          }
          50% {
            transform: scale(1.05);
            opacity: 0.8;
          }
        }

        @keyframes pulse-ring-2 {
          0%, 100% {
            transform: scale(0.9);
            opacity: 0.4;
          }
          50% {
            transform: scale(1.1);
            opacity: 0.6;
          }
        }

        .animate-pulse-ring-1 {
          animation: pulse-ring-1 1.5s ease-in-out infinite;
        }

        .animate-pulse-ring-2 {
          animation: pulse-ring-2 1.5s ease-in-out infinite 0.3s;
        }
      `}</style>
    </div>
  );
};

export default PulseIndicator;
